import { NavLink } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { IconDownload, IconGrid, IconInbox, IconPlay, IconStore, IconUpload, IconUser, IconWave } from '../ui/icons';
import type { ComponentType } from 'react';

interface NavItem {
  to: string;
  label: string;
  Icon: ComponentType<{ size?: number }>;
}

/** Paths shown in the mobile bottom bar (4 slots + the raised upload button). */
const BOTTOM_BAR_PATHS = new Set(['/bench', '/library', '/market', '/account']);

function useItems(): NavItem[] {
  const { t } = useI18n();
  return [
    { to: '/bench', label: t.nav.bench, Icon: IconGrid },
    { to: '/studio', label: t.nav.studio, Icon: IconWave },
    { to: '/vocal', label: t.nav.vocal, Icon: IconPlay },
    { to: '/library', label: t.nav.packs, Icon: IconGrid },
    { to: '/submissions', label: t.nav.submissions, Icon: IconInbox },
    { to: '/market', label: t.nav.market, Icon: IconStore },
    { to: '/creators', label: t.nav.directory, Icon: IconUser },
    { to: '/purchases', label: t.nav.purchases, Icon: IconDownload },
    { to: '/account', label: t.nav.account, Icon: IconUser },
  ];
}

/** Desktop sidebar (§5.9): 240px, surface-1, wordmark, 44px items, lime active state. */
export function Sidebar() {
  const items = useItems();
  const { t } = useI18n();
  return (
    <nav
      aria-label={t.common.brand}
      className="hidden w-60 shrink-0 flex-col border-r border-border-strong bg-surface-1 lg:flex"
    >
      <div className="flex flex-col gap-0.5 px-3 py-6">
        <span className="type-heading-2 text-primary px-2">{t.common.brand}</span>
        <span className="type-caption text-muted px-2">{t.common.tagline}</span>
      </div>
      <ul className="flex flex-1 flex-col gap-1 px-3">
        {items.map(({ to, label, Icon }) => (
          <li key={to}>
            <NavLink
              to={to}
              className={({ isActive }) =>
                [
                  'flex h-11 items-center gap-3 rounded-sm px-3 type-label transition-colors duration-fast ease-machine focus-ring',
                  isActive
                    ? 'bg-accent-tint text-accent border-l-2 border-accent'
                    : 'text-secondary hover:bg-surface-3 hover:text-primary border-l-2 border-transparent',
                ].join(' ')
              }
            >
              <Icon size={20} />
              {label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** Mobile bottom bar (§5.9): 4 items + a raised center square upload button. 56px + safe area. */
export function BottomBar() {
  const bar = useItems().filter((i) => BOTTOM_BAR_PATHS.has(i.to));
  const { t } = useI18n();
  const [left, right] = [bar.slice(0, 2), bar.slice(2)];

  const item = ({ to, label, Icon }: NavItem) => (
    <NavLink
      key={to}
      to={to}
      className={({ isActive }) =>
        [
          'relative flex flex-1 flex-col items-center justify-center gap-1 pt-2 type-nav transition-colors duration-fast ease-machine',
          isActive ? 'text-accent' : 'text-secondary',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          {isActive ? <span className="absolute inset-x-4 top-0 h-0.5 bg-accent" aria-hidden /> : null}
          <Icon size={22} />
          <span>{label}</span>
        </>
      )}
    </NavLink>
  );

  return (
    <nav
      aria-label={t.common.brand}
      className="fixed inset-x-0 bottom-0 z-40 flex h-14 items-stretch border-t border-border-strong bg-surface-1 pb-[env(safe-area-inset-bottom)] lg:hidden"
    >
      {left.map(item)}
      <div className="relative flex w-16 shrink-0 items-start justify-center">
        <NavLink
          to="/studio"
          aria-label={t.nav.upload}
          className="focus-ring -mt-5 flex h-12 w-12 items-center justify-center rounded bg-accent text-ink shadow-overlay transition-colors duration-fast ease-machine hover:bg-accent-hover active:bg-accent-press"
        >
          <IconUpload size={24} />
        </NavLink>
      </div>
      {right.map(item)}
    </nav>
  );
}
